import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getMachineByIdOptions } from "@/client/@tanstack/react-query.gen";
import { MachineData, MachineDetails } from "@/client";
import placeholder from "@/assets/machine-placeholder.svg";
import { CollapsibleCard } from "./collapsible-card";
import { Card, CardContent } from "./ui/card";
import { Separator } from "./ui/separator";
import { Skeleton } from "./ui/skeleton";
import { useTranslation } from "react-i18next";

// Props for the original MachineCard (used in index.tsx)
interface OriginalMachineCardProps extends React.ComponentProps<"div"> {
  machine: MachineDetails;
}

// Props for the new CollapsibleMachineCard (used in cart-list.tsx)
interface CollapsibleMachineCardProps {
  machineId: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  className?: string;
  itemCount?: number;
  itemCountLabel?: string;
}

// Original MachineCard component (for index.tsx)
function MachineCard({ machine, ...props }: OriginalMachineCardProps) {
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const [imageError, setImageError] = React.useState(false);
  const imageUrl = machine.previewFileInfo?.url ?? placeholder;

  return (
    <Card {...props}>
      <CardContent>
        <div className="w-full h-full aspect-square relative">
          {!imageLoaded && !imageError && (
            <Skeleton className="w-full h-full aspect-square object-contain absolute top-0 left-0" />
          )}
          <img
            src={imageUrl}
            alt={machine.name}
            className={`w-full h-full object-contain aspect-square transition-opacity duration-300 ${imageLoaded ? "opacity-100" : "opacity-0"}`}
            onLoad={() => setImageLoaded(true)}
            onError={() => {
              setImageError(true);
              setImageLoaded(true);
            }}
            style={{ position: "absolute", top: 0, left: 0 }}
          />
        </div>
        <Separator className="my-2"></Separator>
        <h3 className="text-xl font-semibold">{machine.name}</h3>
      </CardContent>
    </Card>
  );
}

// New CollapsibleMachineCard component (for cart-list.tsx)
function CollapsibleMachineCard({ 
  machineId, 
  children, 
  defaultExpanded = true,
  onExpandedChange,
  className,
  itemCount,
  itemCountLabel
}: CollapsibleMachineCardProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  
  // First check if machine data is in cache
  const cachedMachineData = queryClient.getQueryData<MachineData>(
    getMachineByIdOptions({ path: { machine_id: machineId } }).queryKey
  );
  
  // Only fetch if not in cache
  const { data: fetchedMachineData, isLoading, error } = useQuery({
    ...getMachineByIdOptions({ path: { machine_id: machineId } }),
    enabled: !!machineId && !cachedMachineData,
  });
  
  // Use cached data if available, otherwise use fetched data
  const machineData = cachedMachineData || fetchedMachineData;

  // Show loading state only when fetching and no cached data available
  if (isLoading && !cachedMachineData) {
    return (
      <CollapsibleCard
        className={className}
        unboundedContent
        title={
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
              <span className="text-xs font-mono text-muted-foreground">
                {machineId.slice(0, 4)}
              </span>
            </div>
            <h3 className="text-lg font-semibold truncate">{t("machineCard.machine")} {machineId}</h3>
            <p className="text-sm text-muted-foreground truncate">
              {isLoading ? t("machineCard.loading") : error ? t("machineCard.errorLoading") : t("machineCard.loading")}
            </p>
            {itemCount !== undefined && itemCountLabel && (
              <span className="text-sm text-muted-foreground ml-auto flex-shrink-0">
                {itemCount} {itemCountLabel}
              </span>
            )}
          </div>
        }
        collapsible
        defaultExpanded={defaultExpanded}
        onExpandedChange={onExpandedChange}
        showSeparator={false}
      >
        {children}
      </CollapsibleCard>
    );
  }

  // Fallback when no data is available (neither cached nor fetched)
  if (!machineData) {
    return (
      <CollapsibleCard
        className={className}
        unboundedContent
        title={
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
              <span className="text-xs font-mono text-muted-foreground">
                {machineId.slice(0, 4)}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold truncate">{t("machineCard.machine")} {machineId}</h3>
              <p className="text-sm text-muted-foreground truncate">
                {error ? t("machineCard.errorLoading") : t("machineCard.machineNotFound")}
              </p>
            </div>
            {itemCount !== undefined && itemCountLabel && (
              <span className="text-sm text-muted-foreground ml-auto flex-shrink-0">
                {itemCount} {itemCountLabel}
              </span>
            )}
          </div>
        }
        collapsible
        defaultExpanded={defaultExpanded}
        onExpandedChange={onExpandedChange}
        showSeparator={false}
      >
        {children}
      </CollapsibleCard>
    );
  }

  return (
    <CollapsibleCard
      className={className}
      unboundedContent
      title={
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden">
            <img
              src={machineData.previewFileInfo?.url ?? placeholder}
              alt={machineData.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold truncate">{machineData.name}</h3>
            <p className="text-sm text-muted-foreground truncate">{machineData.type}</p>
          </div>
        {itemCount !== undefined && itemCountLabel && (
            <span className="text-sm text-muted-foreground ml-auto flex-shrink-0">
              {itemCount} {itemCountLabel}
            </span>
          )}
        </div>
      }
      collapsible
      defaultExpanded={defaultExpanded}
      onExpandedChange={onExpandedChange}
      showSeparator={false}
    >
      {children}
    </CollapsibleCard>
  );
}

// Placeholder component
export function MachineCardPlaceholder({ ...props }: React.ComponentProps<"div">) {
  return (
    <Card {...props}>
      <CardContent>
        <Skeleton className="w-full h-full aspect-square" />
        <Separator className="my-2" />
        <Skeleton className="h-6 w-3/4" />
      </CardContent>
    </Card>
  );
}

// Export both components
export default MachineCard;
export { CollapsibleMachineCard };